import type { EChartsOption } from 'echarts';
import { BarChart, GaugeChart, GraphChart, LineChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useRef } from 'react';

echarts.use([
  BarChart,
  GaugeChart,
  GraphChart,
  LineChart,
  PieChart,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
]);

/** True if any series in the option is a bar series — ECharts auto-defaults
 *  `tooltip.axisPointer` to `'shadow'` (a grey band across the full row)
 *  whenever a bar series is present and `trigger: 'axis'` is set, even if
 *  the option never mentions `axisPointer` at all. */
function hasBarSeries(option: EChartsOption): boolean {
  const series = option.series;
  const list = Array.isArray(series) ? series : series ? [series] : [];
  return list.some((s) => s.type === 'bar');
}

/** Suppresses the bar-chart hover "shadow" band by default, across every
 *  chart, unless a chart explicitly requests its own `axisPointer` type.
 *  Line charts are untouched — their default crosshair `'line'` indicator
 *  isn't the bug this guards against, so it's left as ECharts' own default. */
function withDefaultAxisPointer(option: EChartsOption): EChartsOption {
  const { tooltip } = option;
  if (!tooltip || Array.isArray(tooltip) || tooltip.axisPointer?.type) return option;
  if (!hasBarSeries(option)) return option;

  return {
    ...option,
    tooltip: { ...tooltip, axisPointer: { ...tooltip.axisPointer, type: 'none' } },
  };
}

/**
 * Prevents ECharts' default hover "blur" from fading every OTHER series to a
 * dim opacity as soon as the cursor enters the chart. Root cause (traced
 * through `echarts/lib/core/echarts.js` `updateDirectly` +
 * `echarts/lib/util/states.js` `blurSeriesFromHighlightPayload`): with
 * `tooltip.trigger: 'axis'`, every mousemove dispatches a `highlight` action
 * for the data point nearest the cursor on EVERY series (that's also how the
 * axis tooltip lists every series' value), and ECharts' core action handler
 * unconditionally blurs all *other* series in response — gated only by
 * `series.emphasis.disabled`, completely independent of `blur`/`focus`
 * styling. So overriding `blur.itemStyle`/`blur.lineStyle` opacity (tried
 * first; kept below as a defensive fallback) never touched the actual cause.
 * `emphasis.disabled: true` skips that blur dispatch entirely. Left alone for
 * any series that defines its own `emphasis`/`blur`.
 */
// `blur`/`emphasis`'s shape varies across ECharts' full series union (most
// share `{ itemStyle: { opacity } }` blur and a plain `disabled` emphasis
// flag, but e.g. parallel series don't) — treated structurally since this
// only needs a presence check + shallow default. The result is cast back to
// the series union below: this module only ever registers bar/line/gauge
// series (see `echarts.use()` above), which all share that shape.
function clearBlur(s: { blur?: unknown; emphasis?: unknown }) {
  const withBlur = s.blur
    ? s
    : { ...s, blur: { itemStyle: { opacity: 1 }, lineStyle: { opacity: 1 } } };
  return withBlur.emphasis ? withBlur : { ...withBlur, emphasis: { disabled: true } };
}

function withNoHoverFade(option: EChartsOption): EChartsOption {
  const series = option.series;
  if (!series) return option;

  const updated = Array.isArray(series) ? series.map(clearBlur) : clearBlur(series);

  return {
    ...option,
    series: updated as NonNullable<EChartsOption['series']>,
  };
}

/**
 * Lightweight hook that manages an ECharts instance on a container element.
 * Handles init, option updates, and cleanup/resize automatically.
 */
export function useECharts(option: EChartsOption) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  // Init + update option
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    chartRef.current ??= echarts.init(el);
    chartRef.current.setOption(withNoHoverFade(withDefaultAxisPointer(option)), true);
  }, [option]);

  // Resize observer + window resize — covers both natural container reflows
  // (ResizeObserver) and layout shifts from panel open/close (window 'resize').
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleResize = () => {
      chartRef.current?.resize();
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(el);
    window.addEventListener('resize', handleResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  return containerRef;
}
